/**
 * <PageTemplate> — the standard page shell (template, L0.1).
 *
 * Safe area, centered max-width column, padding, optional header.
 * Bottom tab is OFF by default — production tab roots live under a navigator
 * `tabBar` (e.g. `ParentTabBar`). Pass `showBottomTab` only in Storybook / harnesses.
 *
 * Loading / error replace children only (header + optional tab stay):
 * loading → the page's `skeleton`; error → `<Banner>` (+ optional retry `<Button>`).
 *
 * **Pas de roue.** Une page qui charge passe sa forme : sans `skeleton`, la zone de contenu reste
 * vide — c'est un oubli, et le test de garde (`loadingIndicators.test.ts`) le signale.
 *
 * Le squelette : la page le passe, le gabarit le pose sous UN `<SkeletonGroup>` (une animation par
 * page) et ne le montre qu'une fois le seuil d'apparition passé (`<DelayedSkeleton>`) — avant,
 * la zone de contenu reste vide. Qu'une page ne le montre qu'à son PREMIER chargement n'est pas
 * l'affaire du gabarit : c'est son `loading` qui le dit, calculé par le view-model.
 *
 * Le fond : la couleur de page, SAUF sous un `<ScreenBackground>` — la coquille a déjà peint
 * l'écran (et, sur l'accueil, la lueur), et un fond opaque de page la couvrirait. Aucune prop :
 * ce n'est pas à une page de choisir son fond, c'est à l'écran qui la porte.
 */
import React, { createContext, useContext } from "react"
import {
    Dimensions,
    KeyboardAvoidingView,
    Platform,
    RefreshControl,
    SafeAreaView,
    useWindowDimensions,
    View,
    type NativeScrollEvent,
    type NativeSyntheticEvent,
    type ScrollViewProps,
    type StyleProp,
    type ViewStyle,
} from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SpacingKey } from "../../theme/tokens/spacing"
import { Banner } from "../../atoms/Banner"
import { ScrollView } from "../../atoms/ScrollView"
import { Button } from "../../atoms/Buttons/Button"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { Stack } from "../../atoms/containers/Stack"
import { BottomTab } from "../../molecules/BottomTab"
import { TABS_BY_TYPE, type TabKey, type TabType } from "../../molecules/BottomTab/tabRegistry"
import { DimensionsContext } from "../../../common/utils/mobileUtils"
import { ScreenPaintedContext } from "../ScreenBackground/screenPainted"
import { usePulledRefresh } from "../../hooks/usePulledRefresh"
import { useFocusedFieldInView } from "../../hooks/useFocusedFieldInView"
import { FLOATING_RESERVE_GAP, useFloatingBase, useFloatingReserve } from "./FloatingSlot"

export type PageTemplateProps = {
    children: React.ReactNode
    /** Optional fixed header above the scrollable content. */
    header?: React.ReactNode
    /**
     * Show the baked-in bottom bar — defaults to **false** (navigator owns tabs in production).
     * Opt in for Storybook / DS harnesses only.
     */
    showBottomTab?: boolean
    /** Persona tab set when `showBottomTab` — defaults to `"parent"`. */
    tabType?: TabType
    /** Active tab + change handler when `showBottomTab`. */
    activeKey?: TabKey
    onTabChange?: (key: TabKey) => void
    /** Premier chargement : `skeleton` remplace `children` (la tête et la barre d'onglets restent). */
    loading?: boolean
    /**
     * La forme de la page pendant son premier chargement — des `<Skeleton>` posés là où tombera le
     * contenu. Toute page qui passe `loading` la passe aussi : il n'y a pas de roue de repli.
     */
    skeleton?: React.ReactNode
    /** Content-area error — shown when set and not `loading`. */
    error?: unknown
    onRetry?: () => void
    /** Scroll the content — defaults to true. */
    scroll?: boolean
    /** Forwarded to the content `ScrollView` when `scroll` is true (e.g. NPS scroll triggers). */
    onScroll?: (event: NativeSyntheticEvent<NativeScrollEvent>) => void
    /** Forwarded to the content `ScrollView` — defaults to `400` when `onScroll` is set. */
    scrollEventThrottle?: number
    /**
     * Tirer pour rafraîchir, sur le `ScrollView` du contenu (`scroll` vrai) : `onRefresh` part au geste,
     * `refreshing` dit une relecture en cours — la roue native, elle, ne tourne qu'après un tirer
     * (`usePulledRefresh`). Absent : pas de geste. La même relecture que la flèche du header
     * d'une page, quand elle en a une ; au web, sans geste au pointeur, la flèche reste le moyen.
     */
    refreshing?: boolean
    onRefresh?: () => void
    /** Forwarded to the content `ScrollView` — `"handled"` for a page whose field keeps its keyboard up. */
    keyboardShouldPersistTaps?: ScrollViewProps["keyboardShouldPersistTaps"]
    /** Horizontal side padding — spacing token, defaults to `"3"`. */
    padding?: SpacingKey
    /**
     * Largeur maximale de la colonne de contenu, en px. Rien par défaut : la page garde la largeur
     * que `DimensionsContext` lui donne (les deux tiers de la fenêtre au-delà de 800 px). Les
     * pages parent passent 1120 — au-delà, une ligne devient trop longue à lire, et la règle des
     * deux tiers laisserait la colonne grandir sans fin sur un 27 pouces.
     */
    maxContentWidth?: number
    contentContainerStyle?: StyleProp<ViewStyle>
    /**
     * Bandeau FIXE sous le contenu, au-dessus de la barre d'onglets — le geste qui doit rester
     * sous le pouce pendant que la page défile : le bouton d'envoi d'un formulaire long, la barre
     * « Archiver (3) » d'une sélection. Dans le flux, pas en absolu : il ne recouvre jamais la
     * dernière ligne. Le gabarit dessine sa surface et son filet, et le borne à la même colonne
     * que le contenu : à 1 280 px, un bouton étalé sur toute la fenêtre n'appartiendrait plus au
     * formulaire qu'il valide. Rien par défaut.
     */
    footer?: React.ReactNode
    /**
     * La hauteur du bandeau de pied, à chaque mise en page : ce qui se pose par-dessus la page (un
     * bandeau passager) s'ancre au-dessus de lui. Rien par défaut.
     */
    onFooterLayout?: (height: number) => void
    /**
     * Sous une `<FloatingSlot>` (ce qui flotte au coin bas d'un enchaînement de pages, la bulle tandem
     * du live), le gabarit lui garde sa place au pied de sa zone de contenu : le contenu, qu'il défile
     * ou non, s'arrête au-dessus d'elle, et aucun bouton du bas ne passe dessous, à aucun moment du
     * défilement. Défaut `true`. `false` : la page la garde elle-même (`<StudentActivityPage>`, sous
     * l'énoncé et dans la colonne de la copie). Hors d'une fente, rien n'est réservé.
     */
    reserveFloating?: boolean
    /**
     * La page porte un formulaire qu'on valide clavier ouvert : un appui sur son bouton agit du
     * premier coup au lieu de seulement fermer le clavier, et sur iOS le contenu défile au-dessus
     * du clavier, et le pied (`footer`) s'y lève, comme celui d'une `<Modal>` : le pavé numérique
     * d'iOS n'a pas de touche Retour, et sans ça il cachait « Suivant ». Android s'arrête déjà au
     * clavier (`SystemBarsFrame`). Défaut `false` — le défilement d'avant.
     */
    keyboardAware?: boolean
    testID?: string
    /** Un compteur par onglet, quand `showBottomTab`. Aucun par défaut. */
    tabBadges?: Partial<Record<TabKey, number>>
    /**
     * Le jeu d'onglets quand il dépend des droits (le masteur sans mission n'a pas « Offres de
     * cours »). Défaut : `TABS_BY_TYPE[tabType]`.
     */
    tabs?: TabKey[]
}

/**
 * La largeur du volet qui porte la page, quand ce n'est pas la fenêtre : le volet de l'activité du
 * live (`<StudentLivePage>`). La colonne et le pied de la page s'y bornent, en plus de leur propre
 * borne : sans lui, une page de 560 ou de 760 px gardait sa colonne entière dans un volet de 420, et
 * la page débordait de la fenêtre. Rien par défaut : la page se mesure sur la fenêtre.
 */
export const PaneWidthContext = createContext<number | null>(null)

/**
 * La colonne des pages de l'espace parent — 1120 px, centrée, la largeur de la maquette validée.
 * Les pages parent sortent ainsi de la règle des deux tiers de l'app : elles sont des tableaux de
 * bord, pas des colonnes de téléphone étirées.
 */
export const PARENT_CONTENT_WIDTH = 1120

function errorMessage(error: unknown): string {
    if (typeof error === "string" && error.trim()) return error
    if (typeof error === "object" && error !== null && "message" in error) {
        const msg = (error as { message?: unknown }).message
        if (typeof msg === "string" && msg.trim()) return msg
    }
    return "Oups ! Un problème est survenu"
}

export function PageTemplate(props: PageTemplateProps) {
    const {
        children,
        header,
        showBottomTab = false,
        tabType = "parent",
        activeKey,
        onTabChange,
        loading = false,
        skeleton,
        error,
        onRetry,
        scroll = true,
        onScroll,
        scrollEventThrottle,
        refreshing,
        onRefresh,
        keyboardShouldPersistTaps,
        padding = "3",
        maxContentWidth,
        contentContainerStyle,
        footer,
        onFooterLayout,
        reserveFloating = true,
        keyboardAware = false,
        testID,
        tabBadges,
        tabs,
    } = props
    // Au web, pas de tirer au pointeur : la flèche du header d'une page reste le moyen.
    const pull = usePulledRefresh(refreshing ?? false, onRefresh)
    // La place de ce qui flotte au-dessus des pages (`<FloatingSlot>`), et le pied au-dessus duquel il se pose.
    const floatingRoom = useFloatingReserve()
    const reserve = reserveFloating ? floatingRoom : 0
    const footerBase = useFloatingBase(footer != null)
    const painted = useContext(ScreenPaintedContext)
    const dimensions = useContext(DimensionsContext) ?? Dimensions.get("window")
    const window = useWindowDimensions()
    const resolvedTabs = tabs ?? TABS_BY_TYPE[tabType]

    // Sans `maxContentWidth`, la page suit la règle de l'app (deux tiers de l'écran au-delà de
    // 800 px). Avec, elle se mesure sur la FENÊTRE : une page qui borne sa colonne n'a plus besoin
    // de la règle des deux tiers, qui la rendrait plus étroite que sa propre borne. Posée dans un
    // volet, elle tient aussi dans le volet.
    const pane = useContext(PaneWidthContext)
    const ownWidth = maxContentWidth != null ? Math.min(window.width, maxContentWidth) : dimensions.width
    const contentWidth = pane != null ? Math.min(pane, ownWidth) : ownWidth

    let inner: React.ReactNode = children
    if (error != null && !loading) {
        inner = (
            <Stack gap="4" align="center" style={styles.stateBlock}>
                <Banner message={errorMessage(error)} color="danger" icon="exclamation-circle" tinted />
                {onRetry != null && <Button label="Réessayer" onPress={onRetry} />}
            </Stack>
        )
    } else if (loading) {
        inner = skeleton != null ? <DelayedSkeleton>{skeleton}</DelayedSkeleton> : null
    }

    // Le pied levé prend le mécanisme de `<Modal>` seul : l'inset clavier du défilement, en plus, poussait le
    // champ touché sous le pied.
    const liftFooter = keyboardAware && footer != null && Platform.OS === "ios"
    const scrollRef = useFocusedFieldInView(liftFooter)

    const body = (
        <View style={[styles.centered(contentWidth), styles.viewContent(padding), !scroll && contentContainerStyle]}>
            {inner}
        </View>
    )

    const content = scroll ? (
        <ScrollView
            ref={scrollRef}
            style={styles.flex}
            contentContainerStyle={[styles.scrollContent, contentContainerStyle]}
            showsVerticalScrollIndicator={false}
            onScroll={onScroll}
            scrollEventThrottle={onScroll != null ? (scrollEventThrottle ?? 400) : scrollEventThrottle}
            // Une page-formulaire garde les touches au clavier ouvert ; sinon, le réglage de l'appelant.
            keyboardShouldPersistTaps={keyboardAware ? "handled" : keyboardShouldPersistTaps}
            automaticallyAdjustKeyboardInsets={(keyboardAware && !liftFooter) || undefined}
            refreshControl={
                pull.onRefresh != null ? (
                    <RefreshControl refreshing={pull.refreshing} onRefresh={pull.onRefresh} />
                ) : undefined
            }
        >
            {body}
        </ScrollView>
    ) : (
        <View style={styles.flex}>{body}</View>
    )

    const zone = (
        <>
            {/* Toujours enveloppé : ce qui flotte peut aller et venir sans remonter le contenu. Sa place
                est gardée au pied de la zone, hors de ce qui défile : réservée en fin de défilement
                seulement, elle laissait le bouton des félicitations sous la bulle tant qu'on n'avait
                pas défilé jusqu'au bout. */}
            <View style={[styles.flex, reserve > 0 && styles.reserve(reserve)]}>{content}</View>
            {footer != null && (
                <View
                    style={styles.footerBar}
                    onLayout={(event) => {
                        const height = event.nativeEvent.layout.height
                        footerBase(height)
                        onFooterLayout?.(height)
                    }}
                >
                    <View style={styles.footerContent(contentWidth, padding)}>{footer}</View>
                </View>
            )}
        </>
    )
    return (
        <SafeAreaView style={styles.fill(painted)} testID={testID}>
            {/* Une page qui borne sa colonne borne aussi son en-tête : `<Header>` se mesure sur ce
                contexte, et sans ça il gardait la règle des deux tiers pendant que le contenu suivait
                `maxContentWidth` — titre étroit au-dessus d'une liste large. */}
            {maxContentWidth != null ? (
                <DimensionsContext.Provider value={{ ...dimensions, width: contentWidth }}>
                    {header}
                </DimensionsContext.Provider>
            ) : (
                header
            )}
            {keyboardAware ? (
                // Toujours le même élément : un pied qui va et vient ne remonte pas le formulaire.
                <KeyboardAvoidingView behavior={liftFooter ? "padding" : undefined} style={styles.flex}>
                    {zone}
                </KeyboardAvoidingView>
            ) : (
                zone
            )}
            {showBottomTab && (
                <BottomTab
                    tabs={resolvedTabs}
                    activeKey={activeKey ?? resolvedTabs[0]}
                    onTabChange={onTabChange ?? (() => {})}
                    badges={tabBadges}
                />
            )}
        </SafeAreaView>
    )
}

const styles = StyleSheet.create((theme) => ({
    fill: (painted: boolean) => ({
        flex: 1,
        backgroundColor: painted ? "transparent" : theme.colors.surface,
    }),
    flex: {
        flex: 1,
    },
    scrollContent: {
        flexGrow: 1,
    },
    centered: (width: number) => ({
        flex: 1,
        alignSelf: "center" as const,
        width,
    }),
    viewContent: (padding: SpacingKey) => ({
        flex: 1,
        paddingHorizontal: theme.spacing[padding],
        paddingVertical: theme.spacing["4"],
    }),
    /** La place de ce qui flotte, au pied de la zone de contenu : sa hauteur et l'écart au-dessus de lui. */
    reserve: (height: number) => ({
        paddingBottom: height + theme.spacing[FLOATING_RESERVE_GAP],
    }),
    stateBlock: {
        flex: 1,
        justifyContent: "center",
        paddingVertical: theme.spacing["8"],
    },
    footerBar: {
        alignItems: "center",
        backgroundColor: theme.colors["surface-raised"],
        borderTopColor: theme.colors.border,
        borderTopWidth: 1,
        paddingVertical: theme.spacing["2"],
    },
    footerContent: (width: number, padding: SpacingKey) => ({
        width,
        paddingHorizontal: theme.spacing[padding],
    }),
}))
